import { Connection, PublicKey, Keypair } from '@solana/web3.js';
import { Program, AnchorProvider, BN } from '@project-serum/anchor';
import { useWallet } from '@solana/wallet-adapter-react';
import idl from '../idl/palm_heist_auction.json';

// Constants
export const PROGRAM_ID = new PublicKey('11111111111111111111111111111111');  // Placeholder - System Program ID
export const AUCTION_SEED = 'auction';
export const DAY_SEED = 'day';
export const USER_SEED = 'user';
export const AFFILIATE_SEED = 'affiliate';
export const SOL_VAULT_SEED = 'sol_vault';

// Helper function to create a connection
export const getConnection = (endpoint) => {
  return new Connection(endpoint, 'confirmed');
};

// Helper function to create an anchor provider
export const getProvider = (connection, wallet) => {
  if (!wallet || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }
  
  return new AnchorProvider(
    connection,
    wallet,
    { commitment: 'confirmed' }
  );
};

// Helper function to load the program
export const getProgram = async (connection, wallet, programId = PROGRAM_ID) => {
  const provider = getProvider(connection, wallet);
  
  // Use the imported IDL instead of fetching from chain
  return new Program(idl, programId, provider);
};

// Helper function to find a PDA
export const findPDA = async (seeds, programId = PROGRAM_ID) => {
  return await PublicKey.findProgramAddress(seeds, programId);
};

// Helper function to convert SOL amount to lamports
export const solToLamports = (sol) => {
  return new BN(sol * 1_000_000_000);
};

// Helper function to convert lamports to SOL
export const lamportsToSol = (lamports) => {
  return lamports / 1_000_000_000;
};

// Helper function to handle errors
export const handleError = (error) => {
  console.error('Transaction error:', error);
  
  // Extract the error message from Anchor error if available
  if (error.logs) {
    const anchorError = error.logs.find(log => log.includes('Error:'));
    if (anchorError) {
      const errorMessage = anchorError.split('Error:')[1].trim();
      return errorMessage;
    }
  }
  
  return error.message || 'An unknown error occurred';
};

// Custom hook to get the auction state PDA
export const useAuctionStatePDA = (tokenMint) => {
  const mintKey = new PublicKey(tokenMint);
  const [auctionPDA, auctionBump] = findPDA([
    Buffer.from(AUCTION_SEED),
    mintKey.toBuffer()
  ]);
  
  return { auctionPDA, auctionBump };
};

// Custom hook to get the day allocation PDA
export const useDayAllocationPDA = (auctionPDA, day) => {
  const [dayPDA, dayBump] = findPDA([
    Buffer.from(DAY_SEED),
    auctionPDA.toBuffer(),
    Buffer.from([day])
  ]);
  
  return { dayPDA, dayBump };
};

// Custom hook to get the user contribution PDA
export const useUserContributionPDA = (auctionPDA, userPubkey) => {
  const [userPDA, userBump] = findPDA([
    Buffer.from(USER_SEED),
    auctionPDA.toBuffer(),
    userPubkey.toBuffer()
  ]);
  
  return { userPDA, userBump };
};

// Custom hook to get the affiliate stats PDA
export const useAffiliateStatsPDA = (auctionPDA, affiliatePubkey) => {
  const [affiliatePDA, affiliateBump] = findPDA([
    Buffer.from(AFFILIATE_SEED),
    auctionPDA.toBuffer(),
    affiliatePubkey.toBuffer()
  ]);
  
  return { affiliatePDA, affiliateBump };
};

// Custom hook to get the SOL vault PDA
export const useSolVaultPDA = (auctionPDA) => {
  const [vaultPDA, vaultBump] = findPDA([
    Buffer.from(SOL_VAULT_SEED),
    auctionPDA.toBuffer()
  ]);
  
  return { vaultPDA, vaultBump };
};
